import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Plus, Search, FileStack } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/context/auth'
import {
  applyLatestAssignmentEvent,
  type Case,
  type CaseEvent,
  type CaseListStructuredSnapshot,
  type CaseStatus,
  STATUS_LABELS,
  STATUS_ORDER,
  deriveCaseListDisplayValues,
  formatDate,
  formatUSD,
} from '@/lib/cases'
import { assigneeNameForEmail } from '@/lib/assignees'
import { StatusBadge } from '@/components/app/StatusBadge'
import { EmptyState } from '@/components/app/EmptyState'

type StructuredDocumentListRow = {
  case_id: string
  document_type: 'cbp_7501'
  updated_at: string
  field_values: CaseListStructuredSnapshot['fields']
}

export function CasesList() {
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const [cases, setCases] = useState<Case[]>([])
  const [caseSnapshots, setCaseSnapshots] = useState<Record<string, CaseListStructuredSnapshot>>({})
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')

  const statusFilters = useMemo(() => {
    const queue = searchParams.get('queue')
    const statusesParam = searchParams.get('statuses')

    if (queue === 'ready') return ['ready'] satisfies CaseStatus[]
    if (queue === 'intake') return ['intake'] satisfies CaseStatus[]

    if (statusesParam) {
      return statusesParam
        .split(',')
        .map((value) => value.trim())
        .filter((value): value is CaseStatus => STATUS_ORDER.includes(value as CaseStatus))
    }

    return []
  }, [searchParams])

  const assignmentFilter = searchParams.get('assignment')
  const unassignedOnly = assignmentFilter === 'new'
  const myCasesOnly = assignmentFilter === 'mine'
  const flaggedOnly = searchParams.get('queue') === 'flagged'

  useEffect(() => {
    if (!user) return
    let active = true
    async function loadCases() {
      const { data } = await supabase
        .from('cases')
        .select('*')
        .eq('agent_id', user.id)
        .order('updated_at', { ascending: false })

      if (!active) return

      const nextCases = (data ?? []) as Case[]
      setCases(nextCases)

      if (nextCases.length === 0) {
        setCaseSnapshots({})
        setLoading(false)
        return
      }

      const caseIds = nextCases.map((kase) => kase.id)
      const { data: assignmentEventRows } = await supabase
        .from('case_events')
        .select('*')
        .eq('kind', 'case.assignment_updated')
        .in('case_id', caseIds)
        .order('occurred_at', { ascending: false })

      if (!active) return

      const assignmentEventsByCase = ((assignmentEventRows ?? []) as CaseEvent[]).reduce<Record<string, CaseEvent[]>>(
        (acc, event) => {
          if (!acc[event.case_id]) acc[event.case_id] = []
          acc[event.case_id].push(event)
          return acc
        },
        {},
      )
      const effectiveCases = nextCases.map(
        (kase) => applyLatestAssignmentEvent(kase, assignmentEventsByCase[kase.id]) ?? kase,
      )
      const { data: snapshotRows } = await supabase
        .from('structured_documents')
        .select('case_id, document_type, updated_at, field_values')
        .eq('document_type', 'cbp_7501')
        .in('case_id', caseIds)
        .order('updated_at', { ascending: false })

      if (!active) return

      const nextSnapshots = ((snapshotRows ?? []) as StructuredDocumentListRow[]).reduce<Record<string, CaseListStructuredSnapshot>>(
        (acc, row) => {
          if (acc[row.case_id]) return acc
          acc[row.case_id] = {
            updatedAt: row.updated_at,
            fields: row.field_values,
          }
          return acc
        },
        {},
      )

      setCases(effectiveCases)
      setCaseSnapshots(nextSnapshots)
      setLoading(false)
    }

    void loadCases()

    return () => {
      active = false
    }
  }, [user])

  const filtered = useMemo(() => {
    let list = cases.map((kase) => ({
      caseRecord: kase,
      display: deriveCaseListDisplayValues(kase, caseSnapshots[kase.id]),
    }))

    if (statusFilters.length > 0) {
      list = list.filter(({ caseRecord }) => statusFilters.includes(caseRecord.status))
    }
    if (flaggedOnly) {
      list = list.filter(({ caseRecord }) => (caseRecord.flagged_count ?? 0) > 0)
    }
    if (unassignedOnly) {
      list = list.filter(
        ({ caseRecord }) =>
          caseRecord.status === 'draft'
          || (!caseRecord.assigned_agent_email?.trim() && !caseRecord.assigned_agent_name?.trim()),
      )
    }
    if (myCasesOnly) {
      list = list.filter(({ caseRecord }) => caseRecord.assigned_agent_email?.trim() === user?.email)
    }
    if (query) {
      const q = query.toLowerCase()
      list = list.filter(
        ({ caseRecord, display }) =>
          caseRecord.case_number.toLowerCase().includes(q) ||
          caseRecord.title.toLowerCase().includes(q) ||
          STATUS_LABELS[caseRecord.status].toLowerCase().includes(q) ||
          display.importer?.toLowerCase().includes(q) ||
          display.port?.toLowerCase().includes(q),
      )
    }
    return list.sort(
      (a, b) => new Date(b.display.updatedAt).getTime() - new Date(a.display.updatedAt).getTime(),
    )
  }, [cases, caseSnapshots, flaggedOnly, myCasesOnly, query, statusFilters, unassignedOnly, user?.email])

  function buildNewFilterHref() {
    const nextParams = new URLSearchParams(searchParams)
    if (unassignedOnly) {
      nextParams.delete('assignment')
    } else {
      nextParams.delete('queue')
      nextParams.delete('statuses')
      nextParams.set('assignment', 'new')
    }
    const nextQuery = nextParams.toString()
    return nextQuery ? `/cases?${nextQuery}` : '/cases'
  }

  function buildStatusFilterHref(status: CaseStatus) {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('assignment')
    nextParams.delete('queue')

    const nextStatuses = statusFilters.includes(status)
      ? statusFilters.filter((value) => value !== status)
      : [...statusFilters, status]

    if (nextStatuses.length > 0) {
      nextParams.set('statuses', nextStatuses.join(','))
    } else {
      nextParams.delete('statuses')
    }

    const nextQuery = nextParams.toString()
    return nextQuery ? `/cases?${nextQuery}` : '/cases'
  }

  function buildClearFiltersHref() {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('assignment')
    nextParams.delete('queue')
    nextParams.delete('statuses')
    const nextQuery = nextParams.toString()
    return nextQuery ? `/cases?${nextQuery}` : '/cases'
  }

  const hasActiveFilters = flaggedOnly || unassignedOnly || myCasesOnly || statusFilters.length > 0

  return (
    <section>
      <header className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-primary">Cases</h1>
          <p className="mt-1 text-sm text-neutral-600">
            Every entry summary in flight. {cases.length} total.
          </p>
        </div>
        <Link
          to="/cases/new"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded bg-primary text-white font-semibold hover:bg-primary-dark focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2"
        >
          <Plus size={16} aria-hidden="true" />
          New case
        </Link>
      </header>

      <div className="flex flex-col gap-2">
        <div className="relative flex-1">
          <Search
            size={16}
            aria-hidden="true"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by case number, title, importer, port…"
            className="w-full h-10 pl-9 pr-3 rounded border border-neutral-300 focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2"
            aria-label="Search cases"
          />
        </div>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div
            role="tablist"
            aria-label="Filter cases by status"
            className="flex flex-wrap items-end gap-2 border-b-2 border-secondary/20 pb-0"
          >
            <Link
              role="tab"
              aria-selected={unassignedOnly}
              to={buildNewFilterHref()}
              className={`min-w-[8.75rem] rounded-t-xl border border-b-0 px-4 py-3 text-center text-sm font-semibold transition focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2 ${
                unassignedOnly
                  ? 'border-primary bg-primary text-white shadow-sm'
                  : 'border-neutral-300 bg-secondary-lightest text-primary hover:border-secondary/40 hover:bg-white hover:text-primary-dark'
              }`}
            >
              New
            </Link>
            {STATUS_ORDER.filter((status) => status !== 'draft').map((status) => {
              const isActive = statusFilters.includes(status)
              return (
                <Link
                  key={status}
                  role="tab"
                  aria-selected={isActive}
                  to={buildStatusFilterHref(status)}
                  className={`min-w-[8.75rem] rounded-t-xl border border-b-0 px-4 py-3 text-center text-sm font-semibold transition focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2 ${
                    isActive
                      ? 'border-primary bg-primary text-white shadow-sm'
                      : 'border-neutral-300 bg-secondary-lightest text-primary hover:border-secondary/40 hover:bg-white hover:text-primary-dark'
                  }`}
                >
                  {STATUS_LABELS[status]}
                </Link>
              )
            })}
          </div>
          <div className="flex items-center gap-3 self-end">
            <p className="text-sm font-semibold text-primary">
              Results: <span className="text-secondary">{filtered.length}</span>
            </p>
            <Link
              to={buildClearFiltersHref()}
              aria-disabled={!hasActiveFilters}
              className={`inline-flex h-10 items-center justify-center rounded-md border bg-white px-4 text-sm font-semibold transition focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2 ${
                hasActiveFilters
                  ? 'border-neutral-300 text-primary hover:bg-neutral-50'
                  : 'pointer-events-none border-neutral-200 text-neutral-400'
              }`}
            >
              Clear Filters
            </Link>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-neutral-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6">
            <EmptyState
              Icon={FileStack}
              title={cases.length === 0 ? 'No cases yet' : 'No cases match'}
              body={
                cases.length === 0
                  ? 'Create your first case to start an entry summary.'
                  : 'Try a different filter or search term.'
              }
              action={
                cases.length === 0 ? (
                  <Link
                    to="/cases/new"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded bg-primary text-white font-semibold"
                  >
                    <Plus size={16} aria-hidden="true" /> New case
                  </Link>
                ) : null
              }
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary-lightest text-secondary text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left font-semibold px-4 py-2.5">Case</th>
                  <th className="text-left font-semibold px-4 py-2.5 hidden md:table-cell">Importer</th>
                  <th className="text-left font-semibold px-4 py-2.5 hidden lg:table-cell">Port</th>
                  <th className="text-left font-semibold px-4 py-2.5">Status</th>
                  <th className="text-right font-semibold px-4 py-2.5 hidden sm:table-cell">Value</th>
                  <th className="text-right font-semibold px-4 py-2.5">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200">
                {filtered.map(({ caseRecord, display }) => (
                  <tr key={caseRecord.id} className="hover:bg-neutral-50">
                    <td className="px-4 py-3 align-top">
                      <Link to={`/cases/${caseRecord.id}`} className="block group">
                        <div className="text-xs font-mono text-neutral-500">{caseRecord.case_number}</div>
                        <div className="font-semibold text-primary group-hover:underline">{caseRecord.title}</div>
                        {(!caseRecord.assigned_agent_email && !caseRecord.assigned_agent_name) ? null : (
                          <div className="mt-0.5 text-[11px] text-neutral-500">
                            Assigned to {caseRecord.assigned_agent_name || assigneeNameForEmail(caseRecord.assigned_agent_email) || caseRecord.assigned_agent_email}
                          </div>
                        )}
                        {caseRecord.flagged_count > 0 && (
                          <div className="mt-0.5 text-[11px] text-accent-coral font-semibold">
                            {caseRecord.flagged_count} flag{caseRecord.flagged_count === 1 ? '' : 's'}
                          </div>
                        )}
                      </Link>
                    </td>
                    <td className="px-4 py-3 align-top text-neutral-700 hidden md:table-cell">
                      {display.importer ?? '—'}
                    </td>
                    <td className="px-4 py-3 align-top text-neutral-700 hidden lg:table-cell">
                      {display.port ?? '—'}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <StatusBadge status={caseRecord.status} />
                    </td>
                    <td className="px-4 py-3 align-top text-right font-mono text-neutral-700 hidden sm:table-cell">
                      {formatUSD(display.value)}
                    </td>
                    <td className="px-4 py-3 align-top text-right text-xs text-neutral-500">
                      {formatDate(display.updatedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  )
}
